import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, CheckboxChangeEvent, Tooltip } from 'antd';
import { SessionFormItem } from '@components/Clinicians/Session/Components/SessionFormItem';
import { useSessionFormState } from '@hooks/useSessionFormState';
import { useSessionAction } from '@hooks/useSessionAction';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import { type ApptActionCodes } from 'vl-common/src/schemas/getMetaAppointments';
import { useSessionAppointmentId } from '@hooks/useSessionAppointmentId';
import useAppointmentDetails from '@hooks/appointments/useAppointmentDetails';
import { useEligibilityChecks } from '@hooks/useEligibilityChecks';

export const AuthSelfBookFormItem = ({
  onChange = () => {},
  actionCode
}: {
  onChange?: (value: CheckboxChangeEvent) => void;
  actionCode: ApptActionCodes;
}) => {
  const { t } = useTranslation('common');
  const appt_id = useSessionAppointmentId();
  const { detailsCurrentAppointment } = useAppointmentDetails(appt_id);
  const { SUBTYPE_KEY, AUTH_KEY, AUTH_START_KEY } = useSessionAction(actionCode);

  const { data: eligibilityChecks } = useEligibilityChecks();
  const eligibilityCheckComplete = eligibilityChecks?.eligibility_check_complete;

  const {
    appointment: { appointment_completed: isCompleted, read_only: readonly }
  } = useSessionAppointment();
  const [auth, setAuth] = useSessionFormState(AUTH_KEY);
  const [, setAuthStartDate] = useSessionFormState(AUTH_START_KEY);
  const [subtype] = useSessionFormState(SUBTYPE_KEY);

  const savedAction = detailsCurrentAppointment?.appt_actions?.find(
    ({ appt_action_code }) => appt_action_code === actionCode
  );

  const checkEligibilityForSubtype = useCallback(
    (subtype: string | null | undefined): boolean => {
      if (actionCode !== 'REFF2F') return false;
      return !!(
        (subtype === 'CONSULT' && eligibilityChecks?.F2F_CONSULT_CHECK) ||
        (subtype === 'PHYSIO' && eligibilityChecks?.F2F_PHYSIO_CHECK)
      );
    },
    [eligibilityChecks]
  );

  const [actionSubtypeHasEligibilityCheck, setActionSubtypeHasEligibilityCheck] = useState(
    checkEligibilityForSubtype(savedAction?.action_subtype_code)
  );

  useEffect(() => {
    if (actionCode === 'REFF2F') {
      setActionSubtypeHasEligibilityCheck(checkEligibilityForSubtype(subtype));
    }
  }, [subtype]);

  // placed in useEffect instead of onClick as we need it to trigger on render
  // in case the eligibility checks change after auth been ticked
  useEffect(() => {
    if (actionSubtypeHasEligibilityCheck && actionCode === 'REFF2F') {
      setAuth(false);
      setAuthStartDate(undefined);
    }
  }, [actionSubtypeHasEligibilityCheck, setAuth, setAuthStartDate]);

  return (
    <SessionFormItem label={t('Allow patient to self book?')} name={AUTH_KEY} data-testid="auth-checkbox">
      <Tooltip
        placement="right"
        title={
          actionSubtypeHasEligibilityCheck || !eligibilityCheckComplete
            ? 'Self book cannot be granted due to the eligibility of this case'
            : undefined
        }
        trigger={['hover', 'focus']}
      >
        <Checkbox
          disabled={isCompleted || readonly || actionSubtypeHasEligibilityCheck || !eligibilityCheckComplete}
          onChange={(e) => {
            setAuth(e.target.checked);
            onChange(e);
          }}
          checked={auth}
          defaultChecked={auth}
          data-testid="allow-patient-self-book"
          aria-label={t('Allow patient to self book?')}
        />
      </Tooltip>
    </SessionFormItem>
  );
};
